import { type Persona } from "@kmu-trexpert/shared";

/**
 * Dev persona list for the local persona switcher (decision D2). Mirrors the
 * oracle's accounts. A Trexpert persona lands on the Mandate list; a customer
 * persona lands directly in their own Mandate workspace (decision D1), so each
 * customer carries the `mandateId` of their Mandate. Dev-only — production
 * identity comes from IAM-CDS, not this list.
 *
 * Mandate IDs are UUIDs matching the constants in `@kmu-trexpert/demo/ids.ts`.
 *
 * `id` is the persona's {@link PersonId} — the IAM-CDS user id, which is what
 * the seed's audit fields (`advisor`, `providedBy`, …) store. It must match the
 * corresponding `*_ID` constant in `@kmu-trexpert/demo/ids.ts`; this package
 * cannot import them (no dependency on the demo package), so the values are
 * duplicated here. A mismatch fails BDD immediately — an unknown persona id
 * resolves to no identity, and every scenario 401s.
 */
export interface DevPersona {
    id: string;
    name: string;
    /** The coarse side the persona acts on — drives nav + routing. */
    role: "trexpert" | "customer";
    /** The fine IAM persona role, shown in the persona switcher. */
    personaRole: Persona;
    /** The customer's own Mandate (their direct landing). Absent for Trexpert. */
    mandateId?: string;
    /** The customer's company, shown in the sidebar portal label («Kundenportal
        <companyName>»). Absent for Trexpert. Dev-only — IAM supplies this in prod. */
    companyName?: string;
}

export const DEV_PERSONAS: DevPersona[] = [
    {
        id: "theresa.treuhand@kmu-trexpert.ch",
        name: "Theresa Treuhand",
        role: "trexpert",
        personaRole: "trexpert-clerk",
    },
    {
        id: "markus.mandatt@kmu-trexpert.ch",
        name: "Markus Mandatt",
        role: "trexpert",
        personaRole: "trexpert-admin",
    },
    {
        id: "hugo.holzer@holzbau-keller.ch",
        name: "Hugo Holzer",
        role: "customer",
        personaRole: "customer-admin",
        mandateId: "d0000000-0000-4000-a000-000000000001",
        companyName: "Holzbau Keller GmbH",
    },
    {
        id: "petra.papierkram@holzbau-keller.ch",
        name: "Petra Papierkram",
        role: "customer",
        personaRole: "customer-clerk",
        mandateId: "d0000000-0000-4000-a000-000000000001",
        companyName: "Holzbau Keller GmbH",
    },
    {
        id: "bella.bistro@dolce-vita.ch",
        name: "Bella Bistro",
        role: "customer",
        personaRole: "customer-admin",
        mandateId: "d0000000-0000-4000-a000-000000000002",
        companyName: "Dolce Vita Gastro AG",
    },
    {
        id: "paul.planer@architektur-brunner.ch",
        name: "Paul Planer",
        role: "customer",
        personaRole: "customer-clerk",
        mandateId: "d0000000-0000-4000-a000-000000000006",
        companyName: "Architektur Brunner",
    },
];

export const findPersona = (id: string | null): DevPersona | undefined =>
    DEV_PERSONAS.find(persona => persona.id === id);
